import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Paper,
  Autocomplete,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { request } from '../../../../../utils/request';
import Brandcrumb from '../../../../../componentsUiBlock/Brandcrumb';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Brand, CarModel, JsonField, PriceListFormData, Vendor } from '../../../types';



const AddPriceList: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!id);
  const [jsonFields, setJsonFields] = useState<JsonField[]>([]);
  
  // Local state for price fields to handle clearing properly
  const [mrpInput, setMrpInput] = useState<string>('');
  const [purchasePriceInput, setPurchasePriceInput] = useState<string>('');
  
  // Autocomplete states
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [carModels, setCarModels] = useState<CarModel[]>([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingCarModels, setLoadingCarModels] = useState(false);

  const isEditMode = !!id;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<PriceListFormData>({
    defaultValues: {
      status: 'active',
      description: {
        text: '',
        jsonFields: {}
      }
    },
  });

  const mrpValue = watch('mrp');
  const purchasePriceValue = watch('purchasePrice');
  const selectedCarBrand = watch('carBrand');

  // Sync local state with form values
  useEffect(() => {
    if (mrpValue !== undefined && mrpValue !== null) {
      setMrpInput(mrpValue.toString());
    } else {
      setMrpInput('');
    }
  }, [mrpValue]);

  useEffect(() => {
    if (purchasePriceValue !== undefined && purchasePriceValue !== null) {
      setPurchasePriceInput(purchasePriceValue.toString());
    } else {
      setPurchasePriceInput('');
    }
  }, [purchasePriceValue]);

  // Fetch vendors for autocomplete
  const fetchVendors = async () => {
    try {
      setLoadingVendors(true);
      const res = await request({
        url: 'vendor/get-all-vendors',
        method: 'get',
      });
      if (res.status === 'success') {
        setVendors(res.vendors);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoadingVendors(false);
    }
  };

  // Fetch all brands for autocomplete
  const fetchBrands = async () => {
    try {
      setLoadingBrands(true);
      const res = await request({
        url: 'brand/get-all-brands',
        method: 'get',
      });
      if (res.status === "success") {
        setBrands(res.brands);
      }
    } catch (error:any) {
      console.error('Error fetching brands:', error);
      toast.error(error?.message || 'Failed to load brands');
    } finally {
      setLoadingBrands(false);
    }
  };

  // Fetch ALL car models first, then filter based on selected brand
  const fetchAllCarModels = async () => {
    try {
      setLoadingCarModels(true);
      const res = await request({
        url: 'compatibility/get-all-car-models',
        method: 'get',
      });
            
      // Handle different possible response structures
      let modelsData: CarModel[] = [];
      
      if (res.status === 'success') {
        modelsData = res.models;
      } else if (res.success && res.data) {
        modelsData = res.data;
      } else if (Array.isArray(res)) {
        modelsData = res;
      } else if (res.models) {
        modelsData = res.models;
      } else if (res.data) {
        modelsData = res.data;
      }
      
      setCarModels(modelsData);
      
    } catch (error) {
      console.error('Error fetching car models:', error);
      toast.error('Failed to load car models');
    } finally {
      setLoadingCarModels(false);
    }
  };

  // Filter car models based on selected car brand
  const getFilteredCarModels = () => {
    if (!selectedCarBrand) {
      return [];
    }
    
    const filteredModels = carModels.filter((model) => 
      model.brand.name.toLowerCase() === selectedCarBrand.toLowerCase()
    );
    
    return filteredModels;
  };

  // Fetch data for autocomplete fields
  useEffect(() => {
    fetchVendors();
    fetchBrands();
    fetchAllCarModels(); // Load all car models initially
  }, []);

  // Clear car model when car brand changes
  useEffect(() => {
    if (selectedCarBrand) {
      // Car models are already loaded, we'll filter them in the getFilteredCarModels function
    } else {
      setValue('carModel', '');
    }
  }, [selectedCarBrand, setValue]);

  // Fetch existing data for edit mode
  useEffect(() => {
    const fetchPriceItem = async () => {
      if (!id) return;

      try {
        setFetching(true);
        const res = await request({
          url: `priceList/get-one-pricelist/${id}`,
          method: "get",
        });
        
        if (res.success && res.data) {
          const item = res.data;
          
          // Set form values with proper structure
          Object.keys(item).forEach((key) => {
            if (key === 'description') {
              // Handle description object structure
              if (typeof item.description === 'string') {
                // Backward compatibility: convert string to object
                setValue('description', {
                  text: item.description,
                  jsonFields: {}
                });
                setJsonFields([]);
              } else if (item.description && typeof item.description === 'object') {
                // Ensure proper structure
                const descriptionData = {
                  text: item.description.text || '',
                  jsonFields: item.description.jsonFields || {}
                };
                setValue('description', descriptionData);
                
                // Convert jsonFields object to array for form
                const fieldsArray: JsonField[] = [];
                if (descriptionData.jsonFields) {
                  Object.entries(descriptionData.jsonFields).forEach(([key, value]) => {
                    fieldsArray.push({
                      key,
                      value: String(value)
                    });
                  });
                }
                setJsonFields(fieldsArray);
              }
            } else if (key in item) {
              setValue(key as keyof PriceListFormData, item[key]);
            }
          });
        } else {
          toast.error('Failed to load price list item');
        }
      } catch (error: any) {
        console.error('Error fetching price item:', error);
        toast.error(error?.message || 'Error loading price list item');
      } finally {
        setFetching(false);
      }
    };

    fetchPriceItem();
  }, [id, setValue]);

  // Add new key-value field
  const addJsonField = () => {
    setJsonFields([...jsonFields, { key: '', value: '' }]);
  };

  // Remove key-value field
  const removeJsonField = (index: number) => {
    const newFields = jsonFields.filter((_, i) => i !== index);
    setJsonFields(newFields);
  };

  // Update key-value field
  const updateJsonField = (index: number, field: 'key' | 'value', newValue: string) => {
    const newFields = [...jsonFields];
    newFields[index][field] = newValue;
    setJsonFields(newFields);
  };

  const calculateMargin = () => {
    if (mrpValue && purchasePriceValue && mrpValue > 0 && purchasePriceValue > 0) {
      const margin = ((mrpValue - purchasePriceValue) / mrpValue) * 100;
      return margin.toFixed(1);
    }
    return null;
  };

  const onSubmit = async (data: PriceListFormData) => {
    try {
      setLoading(true);
      
      // Convert jsonFields array to object
      const jsonFieldsObject: Record<string, any> = {};
      jsonFields.forEach(field => {
        if (field.key.trim() && field.value.trim()) {
          // Try to parse value as number, boolean, or keep as string
          let parsedValue: any = field.value;
          if (!isNaN(Number(field.value))) {
            parsedValue = Number(field.value);
          } else if (field.value.toLowerCase() === 'true') {
            parsedValue = true;
          } else if (field.value.toLowerCase() === 'false') {
            parsedValue = false;
          }
          jsonFieldsObject[field.key.trim()] = parsedValue;
        }
      });

      // Ensure description has proper structure
      const processedData = {
        ...data,
        description: {
          text: data.description?.text || '',
          jsonFields: jsonFieldsObject
        }
      };

      if (isEditMode) {
        // Update existing item
        const response = await request({
          url: `priceList/update-pricelist/${id}`,
          method: 'put',
          data: processedData,
        });

        if (response.success) {
          toast.success(response.message || 'Price list item updated successfully!');
          navigate('/vendors/priceList');
        } else {
          toast.error(response.message || 'Failed to update price list item');
        }
      } else {
        // Create new item
        const response = await request({
          url: 'priceList/create-priceList',
          method: 'post',
          data: processedData,
        });

        if (response.success) {
          toast.success(response.message || 'Price list item added successfully!');
          navigate('/vendors/priceList');
        } else {
          toast.error(response.message || 'Failed to add price list item');
        }
      }
    } catch (error: any) {
      console.error('Error saving price list:', error);
      toast.error(error?.message || `Error ${isEditMode ? 'updating' : 'adding'} price list item`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/vendors/priceList');
  };

  const margin = calculateMargin();

  if (fetching) {
    return (
      <>
        <Box sx={{ mb: 2 }}>
          <Brandcrumb />
        </Box>
        <Card elevation={4}>
          <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </CardContent>
        </Card>
      </>
    );
  }

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Brandcrumb />
        </Box>
      </Box>

      <Card elevation={4} sx={{ margin: '0 auto' }}>
        <CardContent sx={{ p: 2.5 }}>
          {/* Dynamic Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
            <RequestQuoteIcon color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h6" fontWeight="bold">
              {isEditMode ? 'Edit Price List Item' : 'Add New Price List Item'}
            </Typography>
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              
              {/* Row 1: Part No & Product Name */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Controller
                  name="partNo"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label="Part Number"
                      placeholder="Part number"
                      error={!!errors.partNo}
                      helperText={errors.partNo?.message}
                      sx={{ flex: 1 }}
                    />
                  )}
                />

                <Controller
                  name="productName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      label="Product Name"
                      placeholder="Product name"
                      error={!!errors.productName}
                      helperText={errors.productName?.message}
                      sx={{ flex: 2 }}
                    />
                  )}
                />
              </Box>

              {/* Row 2: Vendor & Product Brand */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Controller
                  name="vendorName"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Autocomplete
                      {...field}
                      size="small"
                      options={vendors.map(vendor => vendor.vendorName)}
                      value={value || ''}
                      onChange={(_, newValue) => {
                        onChange(newValue || '');
                      }}
                      loading={loadingVendors}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Vendor"
                          placeholder="Select vendor"
                          error={!!errors.vendorName}
                          helperText={errors.vendorName?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingVendors ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ flex: 1 }}
                    />
                  )}
                />

                <Controller
                  name="productBrand"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Autocomplete
                      {...field}
                      size="small"
                      options={brands
                        .filter(brand => brand.manufactureType.includes('Parts'))
                        .map(brand => brand.name)
                      }
                      value={value || ''}
                      onChange={(_, newValue) => {
                        onChange(newValue || '');
                      }}
                      loading={loadingBrands}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Product Brand"
                          placeholder="Select product brand"
                          error={!!errors.productBrand}
                          helperText={errors.productBrand?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingBrands ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
              </Box>

              {/* Row 3: Car Brand & Model */}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Controller
                  name="carBrand"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Autocomplete
                      {...field}
                      size="small"
                      options={brands
                        .filter(brand => brand.manufactureType.includes('Cars'))
                        .map(brand => brand.name)
                      }
                      value={value || ''}
                      onChange={(_, newValue) => {
                        onChange(newValue || '');
                        // Clear car model when brand changes
                        setValue('carModel', '');
                      }}
                      loading={loadingBrands}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Car Brand"
                          placeholder="Select car brand"
                          error={!!errors.carBrand}
                          helperText={errors.carBrand?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingBrands ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ flex: 1 }}
                    />
                  )}
                />

                <Controller
                  name="carModel"
                  control={control}
                  render={({ field: { onChange, value, ...field } }) => (
                    <Autocomplete
                      {...field}
                      size="small"
                      options={getFilteredCarModels().map(model => model.name)}
                      value={value || ''}
                      onChange={(_, newValue) => {
                        onChange(newValue || '');
                      }}
                      disabled={!selectedCarBrand}
                      loading={loadingCarModels}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Car Model"
                          placeholder={selectedCarBrand ? "Select car model" : "Select car brand first"}
                          error={!!errors.carModel}
                          helperText={errors.carModel?.message}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingCarModels ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ flex: 1 }}
                    />
                  )}
                />
              </Box>

              {/* Row 4: Pricing - COMPLETELY FIXED */}
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                {/* MRP Field */}
                <Box sx={{ flex: 1 }}>
                  <TextField
                    size="small"
                    label="MRP"
                    placeholder="Maximum retail price"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    error={!!errors.mrp}
                    helperText={errors.mrp?.message}
                    fullWidth
                    value={mrpInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setMrpInput(value);
                      
                      if (value === '') {
                        setValue('mrp', undefined);
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          setValue('mrp', numValue, { shouldValidate: true });
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Final validation on blur
                      if (e.target.value === '') {
                        setValue('mrp', undefined, { shouldValidate: true });
                      }
                    }}
                  />
                </Box>

                {/* Purchase Price Field */}
                <Box sx={{ flex: 1 }}>
                  <TextField
                    size="small"
                    label="Purchase Price"
                    placeholder="Purchase price"
                    type="number"
                    inputProps={{ min: 0, step: "0.01" }}
                    error={!!errors.purchasePrice}
                    helperText={errors.purchasePrice?.message}
                    fullWidth
                    value={purchasePriceInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPurchasePriceInput(value);
                      
                      if (value === '') {
                        setValue('purchasePrice', undefined);
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          setValue('purchasePrice', numValue, { shouldValidate: true });
                        }
                      }
                    }}
                    onBlur={(e) => {
                      // Final validation on blur
                      if (e.target.value === '') {
                        setValue('purchasePrice', undefined, { shouldValidate: true });
                      }
                    }}
                  />
                </Box>

                {/* Margin Display - Compact */}
                {margin && (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center',
                    p: 1,
                    border: '1px solid',
                    borderColor: 'success.main',
                    borderRadius: 1,
                    backgroundColor: 'success.light + 08',
                    minWidth: 100,
                    height: 56
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      Margin
                    </Typography>
                    <Chip 
                      label={`${margin}%`} 
                      size="small" 
                      color="success" 
                      variant="filled"
                      sx={{ height: 24, fontSize: '0.75rem' }}
                    />
                  </Box>
                )}
              </Box>

              {/* Row 5: Description Text */}
              <Controller
                name="description.text"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    label="Description"
                    placeholder="Product description (optional)"
                    multiline
                    rows={2}
                    fullWidth
                  />
                )}
              />

              {/* Row 6: JSON Fields - Key Value Pairs */}
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    Additional Properties
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addJsonField}
                    variant="outlined"
                  >
                    Add Property
                  </Button>
                </Box>

                {jsonFields.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No additional properties added. Click "Add Property" to include key-value pairs.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {jsonFields.map((field, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                        <TextField
                          size="small"
                          label="Key"
                          placeholder="Property name"
                          value={field.key}
                          onChange={(e) => updateJsonField(index, 'key', e.target.value)}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size="small"
                          label="Value"
                          placeholder="Property value"
                          value={field.value}
                          onChange={(e) => updateJsonField(index, 'value', e.target.value)}
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeJsonField(index)}
                          color="error"
                          sx={{ mt: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  These properties will be stored as structured data in JSON format in description.
                </Typography>
              </Paper>

              {/* Row 7: Status & Actions */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FormControl size="small" sx={{ width: 140 }}>
                      <InputLabel>Status</InputLabel>
                      <Select {...field} label="Status" size="small">
                        <MenuItem value="active">
                          <Chip 
                            label="Active" 
                            size="small" 
                            color="success" 
                            variant="filled"
                            sx={{ minWidth: 70, height: 24 }}
                          />
                        </MenuItem>
                        <MenuItem value="inactive">
                          <Chip 
                            label="Inactive" 
                            size="small" 
                            color="error" 
                            variant="filled"
                            sx={{ minWidth: 70, height: 24 }}
                          />
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={isEditMode ? () => navigate(-1) : handleCancel}
                    startIcon={<CancelIcon />}
                    disabled={loading}
                    sx={{ minWidth: 100, height: 36 }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <></> : <SaveIcon />}
                    disabled={loading}
                    sx={{ minWidth: 120, height: 36 }}
                  >
                    {loading ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1 }} />
                        {isEditMode ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      isEditMode ? 'Update Item' : 'Save Item'
                    )}
                  </Button>
                </Box>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </>
  );
};

export default AddPriceList;